/** Спільні колонки сальдо/оборотів для стандартних звітів (ч.1036). */
import type { ColumnDef } from '@/shared/components/DataTable';
import type { LedgerBalances } from '@/api/documents';
import { money, qty } from './ledgerModel';

const num = (v: string | number | null | undefined) =>
  <span className="ledger__num">{money(v)}</span>;

/**
 * SUP-321707CF (ч.1239): на екрані 1280 з відкритим лівим меню таблиця має ~970 px. Шість
 * грошових колонок по 120 + назва 360–400 давали 1080–1120 — «Сальдо кін.» ховалось за правий
 * край, і залишок не було видно без прокрутки. Тепер 6 × 108 + назва 320 = 968.
 */
export const MONEY_W = 108;
export const LABEL_W = 320;

export function balanceColumns<T extends LedgerBalances>(): ColumnDef<T>[] {
  return [
    { key: 'opening_dt', header: <>Сальдо поч.<br />Дт</>, width: MONEY_W, align: 'right', cell: (r) => num(r.opening_dt) },
    { key: 'opening_cr', header: <>Сальдо поч.<br />Кт</>, width: MONEY_W, align: 'right', cell: (r) => num(r.opening_cr) },
    { key: 'turnover_dt', header: <>Оборот<br />Дт</>, width: MONEY_W, align: 'right', cell: (r) => num(r.turnover_dt) },
    { key: 'turnover_cr', header: <>Оборот<br />Кт</>, width: MONEY_W, align: 'right', cell: (r) => num(r.turnover_cr) },
    { key: 'closing_dt', header: <>Сальдо кін.<br />Дт</>, width: MONEY_W, align: 'right', cell: (r) => num(r.closing_dt) },
    { key: 'closing_cr', header: <>Сальдо кін.<br />Кт</>, width: MONEY_W, align: 'right', cell: (r) => num(r.closing_cr) },
  ];
}

const qnum = (v: string | number | null | undefined) =>
  <span className="ledger__num">{qty(v)}</span>;

/** Колонки кількості (ч.1037) — лише коли увімкнено «кількість». */
export function quantityColumns<T extends LedgerBalances>(): ColumnDef<T>[] {
  return [
    { key: 'opening_qty', header: <>Кіл.<br />поч.</>, width: 90, align: 'right', cell: (r) => qnum(r.opening_qty) },
    { key: 'turnover_qty_dt', header: <>Кіл.<br />Дт</>, width: 90, align: 'right', cell: (r) => qnum(r.turnover_qty_dt) },
    { key: 'turnover_qty_cr', header: <>Кіл.<br />Кт</>, width: 90, align: 'right', cell: (r) => qnum(r.turnover_qty_cr) },
    { key: 'closing_qty', header: <>Кіл.<br />кін.</>, width: 90, align: 'right', cell: (r) => qnum(r.closing_qty) },
  ];
}

export { num as numCell, qnum as qtyCell };

/** Заголовок звіту з підписом періоду. */
export function periodLabel(from: string, to: string): string {
  const d = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('uk-UA');
  return `за період з ${d(from)} по ${d(to)}`;
}

/** Комірка дерева: відступ за рівнем + трикутник згортання для груп. */
export function TreeLabel({ level, isGroup, collapsed, onToggle, children }: {
  level: number; isGroup: boolean; collapsed: boolean; onToggle: () => void; children: React.ReactNode;
}) {
  return (
    <span style={{ paddingLeft: `${level * 1.25}rem`, display: 'inline-flex', gap: '0.3rem', alignItems: 'baseline' }}>
      <span className="ledger__tree-toggle" title={isGroup ? (collapsed ? 'розгорнути' : 'згорнути') : undefined}
        onClick={(e) => { if (!isGroup) return; e.stopPropagation(); onToggle(); }}>
        {isGroup ? (collapsed ? '▸' : '▾') : ''}
      </span>
      {children}
    </span>
  );
}
